import React, { useEffect, useState } from "react";
import { ExternalDesignProvider } from "./contexts/external-design.context";
import { ExternalLogoPlacer } from "./components/ExternalLogoPlacer";
import { Design, DesignLogo } from "../../../core/models/entities/Design";
import { Style } from "../../../core/models/entities/Style";
import { BaseLogo } from "../../../core/models/entities/BaseLogo";
import { DesignStatus } from "../../../core/models/entities/enums/DesignStatus";
import { Organization } from "../../../core/models/entities/Organization";
import { StyleView } from "../../../core/models/entities/StyleView";
import { EditAction } from "../../../data/models/responses/DesignDto";
import { generateUUID } from "../../../utils/functions/StringUtil";

// Input Types
interface DesignLogoInput {
  view: string;
  logoId: number;
  xCoordinate: number;
  yCoordinate: number;
  angleRads?: number;
}

export interface EditorConfig {
  theme?: {
    primaryColor?: string; // Kept for potential future use or alias
    backgroundColor?: string;
    accentColor?: string; // New accent color
  };
  layout?: {
    viewSelectorPosition?: "top" | "bottom";
    padding?: string; // e.g. "1rem" or "0"
    paddingSelectedView?: string; // Padding for the main image area
    paddingViewSelector?: string; // Padding for the view selector strip
    heightSelectedView?: string; // Height of the main selected image
  };
}

interface EditorInput {
  styleId: number;
  title: string;
  organizationId?: number;
  designLogos: DesignLogoInput[];
}

// Message Type
interface InitMessage {
  type: "INIT_EXTERNAL_DESIGNER";
  apiKey: string;
  payload: EditorInput;
  config?: EditorConfig;
}

const EXTERNAL_API_URL = process.env.REACT_APP_EXTERNAL_API_URL;

export const ExternalDesignEditorScreen: React.FC = () => {
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [inputData, setInputData] = useState<EditorInput | null>(null);
  const [config, setConfig] = useState<EditorConfig | undefined>(undefined);
  const [design, setDesign] = useState<Design | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate event.data structure if needed
      const data = event.data as InitMessage;

      if (data && data.type === "INIT_EXTERNAL_DESIGNER") {
        console.log("Received init message", data);
        setApiKey(data.apiKey);
        setInputData(data.payload);
        setConfig(data.config);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!apiKey || !inputData) return;

      setLoading(true);
      setError(null);

      try {
        // 1. Fetch Style
        const styleResponse = await fetch(
          `${EXTERNAL_API_URL}/styles/${inputData.styleId}/extended`,
          {
            headers: {
              "X-Api-Key": apiKey,
              "Content-Type": "application/json",
            },
          },
        );

        if (!styleResponse.ok) {
          throw new Error(`Failed to fetch style: ${styleResponse.statusText}`);
        }

        const styleData = await styleResponse.json();

        // 2. Fetch Logos
        if (!inputData.designLogos) {
          inputData.designLogos = [];
        }

        const uniqueLogoIds = Array.from(
          new Set(inputData.designLogos.map((l) => l.logoId)),
        );
        const logoPromises = uniqueLogoIds.map(async (logoId) => {
          const logoResponse = await fetch(
            `${EXTERNAL_API_URL}/logo/${logoId}`,
            {
              headers: {
                "X-Api-Key": apiKey,
                "Content-Type": "application/json",
              },
            },
          );

          if (!logoResponse.ok) {
            throw new Error(
              `Failed to fetch logo ${logoId}: ${logoResponse.statusText}`,
            );
          }

          return logoResponse.json();
        });

        const logosData = await Promise.all(logoPromises);

        // 3. Construct Design Object
        const style: Style = {
          id: styleData.id || inputData.styleId,
          name: styleData.name,
          model: styleData.model,
          EAN: styleData.ean || styleData.EAN,
          manufacturerId: styleData.manufacturerId,
          styleViews: (styleData.views || styleData.Views || []).map(
            (v: any) =>
              ({
                id: v.viewId || v.ViewId,
                view: v.view || v.View,
                imageUrl: v.imageUrl || v.ImageUrl,
                maskUrl: v.maskUrl || v.MaskUrl,
                updatedMaskUrl:
                  v.updatedMaskUrl ||
                  v.UpdatedMaskUrl ||
                  v.maskUrl ||
                  v.MaskUrl, // Use maskUrl as initial updatedMaskUrl
                ppcm: v.ppcm || v.Ppcm || 17, // Default to 17 if available
                styleId: styleData.id,
                createdAtUtc: new Date(),
                updatedAtUtc: new Date(),
                createdBy: "System",
                originalImageUrl: v.imageUrl, // Assuming original is same
                maskErrorMessage: "",
              }) as StyleView,
          ),
          manufacturer: { id: styleData.manufacturerId } as any, // Placeholder
          createdAtUtc: new Date(),
          updatedAtUtc: new Date(),
          // createdBy: "System",
          // categories: [],
          // isArchived: false,
          // active: true,
          status: "annotated" as any, // Placeholder
          thumbnailUrl: "",
          modelAndName: `${styleData.model} ${styleData.name}`,
          originalImageUrl: "",
          thumbnailUrlLowResolution: "",
          widthInCm: 0,
          heightInCm: 0,
          modelAndNameWithoutHyphen:
            `${styleData.model} ${styleData.name}`.replace("-", ""),
        };

        const designLogos: DesignLogo[] = [];
        inputData.designLogos.forEach((inputLogo) => {
          const logo = logosData.find(
            (l) => l.logoId === inputLogo.logoId,
          ) as any;

          if (!logo) {
            console.warn(`Logo data not found for ID ${inputLogo.logoId}`);
            return;
          }

          const view = style.styleViews.find(
            (v) => v.view?.toLowerCase() === inputLogo.view?.toLowerCase(),
          );

          if (!view) {
            console.warn(
              `View '${inputLogo.view}' not found in style views. Skipping logo ${inputLogo.logoId}.`,
            );
            return;
          }

          const imageUrl =
            logo.lowResBase64 || logo.LowResBase64
              ? `data:image/png;base64,${
                  logo.lowResBase64 || logo.LowResBase64
                }`
              : "";

          const baseLogo: BaseLogo = {
            ...logo,
            id: logo.logoId || logo.LogoId,
            name: logo.title || logo.Title,
            widthMm: logo.widthInMM || logo.WidthInMM,
            heightMm: logo.heightInMM || logo.HeightInMM,
            displayImage: {
              digitUrl: imageUrl,
              lowResUrl: imageUrl,
              highResUrl: imageUrl,
              originalUrl: imageUrl,
            },
            // Add other missing properties required by BaseLogo if needed
            transferMethod: {
              type: logo.logoType || "Transfer",
              method1: logo.transferMethodKey,
            } as any,
            logoType: logo.logoType,
            colors: logo.colors ? logo.colors.map((c: any) => c.id) : [],
            colorDetails: logo.colors
              ? logo.colors.map((c: any) => ({
                  id: c.id,
                  name: c.name,
                  red: c.red,
                  green: c.green,
                  blue: c.blue,
                }))
              : [],
          } as unknown as BaseLogo;

          let finalX = inputLogo.xCoordinate;
          let finalY = inputLogo.yCoordinate;

          // Logic for missing coordinates:
          // 1. If both missing -> set to -99999 (Sentinel for "Center Me")
          // 2. If one missing -> set that one to 0
          if (finalX === undefined && finalY === undefined) {
            finalX = -99999;
            finalY = -99999;
          } else {
            if (finalX === undefined) finalX = 0;
            if (finalY === undefined) finalY = 0;
          }

          designLogos.push({
            id: 0,
            inDesignId: generateUUID(),
            logoX: finalX,
            logoY: finalY,
            viewId: view.id,
            logo: baseLogo,
            logoId: inputLogo.logoId,
            action: EditAction.Move,
            angleRads: inputLogo.angleRads ?? 0,
            heightMm: baseLogo.heightMm,
            widthMm: baseLogo.widthMm,
            designerWidthMm: baseLogo.widthMm,
            viewName: view.view,
          });
        });

        const initialDesign: Design = {
          id: 0, // Temporary ID
          title: inputData.title,
          styleId: inputData.styleId,
          style: style,
          designLogos: designLogos,
          status: DesignStatus.Draft,
          createdAtUtc: new Date(),
          updateAtUtc: new Date(),
          createdBy: "External User",
          customerId: inputData.organizationId || 0,
          customer: {} as Organization, // Placeholder
          approval: null,
          product: null,
          heating: null,
        };

        setDesign(initialDesign);
      } catch (err: any) {
        console.error("Error fetching data:", err);
        setError(err.message || "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };

    if (apiKey && inputData) {
      fetchData();
    }
  }, [apiKey, inputData]);

  return (
    <div
      className="h-screen w-screen flex flex-col overflow-hidden"
      style={{
        backgroundColor: config?.theme?.backgroundColor || "#f9fafb", // Default gray-50
        padding: config?.layout?.padding || "0",
      }}
    >
      {!apiKey && !loading && (
        <div className="flex items-center justify-center h-full">
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            Waiting for initialization message...
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center h-full">
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
            Loading data...
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center justify-center h-full">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            Error: {error}
          </div>
        </div>
      )}

      {design && apiKey && (
        <ExternalDesignProvider design={design} config={config} apiKey={apiKey}>
          <ExternalLogoPlacer />
        </ExternalDesignProvider>
      )}
    </div>
  );
};
